/**
 * API client for timber pricing endpoints.
 *
 * Proxied through FastAPI at /api/v1/timber-prices/*
 */

import { apiClient } from './client';
import {
  timberPriceReportSnapshotSchema,
  type TimberPriceLatestAssortmentRow,
  type TimberPriceLocalizedLabels,
  type TimberPriceReportPoint,
  type TimberPriceReportSnapshot,
} from './timberPriceReportSnapshot.schema';
import { parsePrice } from '@/components/timber-prices/parsePrice';
import type { Language } from '@/i18n/types';
export { NATIONAL_REGION_CODE } from '@/shared/config/timberPrices';
export type {
  TimberPriceLatestAssortmentRow,
  TimberPriceLocalizedLabels,
  TimberPriceReportPoint,
  TimberPriceReportSnapshot,
};

// Types

export interface WeeklyPricePoint {
  week_code: string;
  week_start_date: string;
  region: string;
  harvest_type: string;
  assortment: string;
  price_eur_per_m3: string;
  source_dataset: string;
}

export interface WeeklyPricesResponse {
  results: {
    data: WeeklyPricePoint[];
    metadata: PriceMetadata;
  };
  count: number;
  next: string | null;
  previous: string | null;
}

export interface LatestPricesData {
  [harvestType: string]: {
    [assortmentCode: string]: string;
  };
}

export interface MovingAveragePoint {
  date: string;
  price: string;
}

export interface PriceMetadata {
  source: string;
  harvest_type_policy_version: string;
  region?: string;
  latest_week?: string;
  data_freshness_hours?: number;
  window_months?: number;
}

export interface CarriedForwardTimberPrice {
  sale_basis: 'standing' | 'roadside';
  price_key: string;
  observation_week: string;
  observation_date: string;
  reason_code: 'no_newer_regional_observation';
}

export interface PricingScenario {
  scenario_id: string;
  window_months: number;
  region_code: string;
  region_name: string;
  prices_by_assortment: Record<string, string>;
  roadside_prices_by_assortment: Record<string, string>;
  carried_forward_prices: CarriedForwardTimberPrice[];
  harvest_type_policy_version: string;
  computed_at: string;
  data_freshness_hours: number;
  latest_week_code: string;
  source: 'user_preference' | 'explicit' | 'default';
}

export interface SimulationContext {
  pricing_scenario: PricingScenario;
  cost_profile_name: string | null;
  cost_profile_version: string | null;
  costs_by_harvest_context: Record<string, string>;
}

export interface SyncStatus {
  latest_sync_at: string | null;
  latest_week_code: string | null;
  data_age_hours: number | null;
  sync_status: 'ok' | 'stale' | 'never_synced' | 'error';
  rows_in_database: number;
}

export interface UserPricePreferences {
  default_window_months: number;
  default_region_code: string | null;
}

/** How a property's price region was established (forest_models PricingRegionProvenance). */
export interface PricingRegionProvenance {
  region_code: string;
  basis:
    | 'stand_municipality'
    | 'property_municipality'
    | 'property_shared_price_region'
    | 'coordinates'
    | 'requested_region'
    | 'municipality_unresolved';
  municipality: string | null;
  /** Present only for the labelled national estimate (basis municipality_unresolved). */
  reason: string | null;
}

export interface ActiveSnapshot {
  id: string;
  operation_cost_snapshot_id: string;
  region_code: string;
  pricing_region: PricingRegionProvenance;
  window_months: WindowMonths;
  materialized_at: string;
  latest_week_code: string;
  scenario: PricingScenario;
}

export interface ActiveSnapshotRevaluationContext {
  snapshot_id: string;
  operation_cost_snapshot_id: string;
  prices_by_assortment: Record<string, string | number>;
  roadside_prices_by_assortment: Record<string, string | number>;
  region_code?: string | null;
  sale_type?: string;
  costs_by_harvest_context?: Record<string, string | number>;
  silvicultural_costs_by_op_type?: Record<string, number | null>;
  pystykauppa_supervision_fee_percent?: string | number | null;
}

export interface ActiveSnapshotPlanningObjective {
  usable: boolean;
  requires_revaluation_context: boolean;
  forest_generate_plan_args: {
    active_snapshot_id?: string;
    revaluation_context?: ActiveSnapshotRevaluationContext;
  };
  guidance: string;
  revaluation_context_available: boolean | null;
  unavailable_reasons: string[] | null;
  unavailable_reason: string | null;
}

export interface ActiveSnapshotPlanning {
  objective_applicability: Record<string, ActiveSnapshotPlanningObjective>;
  forest_generate_plan_args: Record<
    string,
    {
      active_snapshot_id?: string;
      revaluation_context?: ActiveSnapshotRevaluationContext;
    }
  >;
}

/**
 * A selectable LUKE timber-price region with a locale-aware label contract
 * The backend always sends `is_national` and a
 * `fallback_label` (the canonical Finnish source-language name).
 *
 * `labels` is intentionally `Partial`: a locale whose backend column is blank
 * may arrive as an empty string OR be omitted entirely, and the frontend's
 * single fallback rule (`resolveRegionLabel`) is the one place that resolves
 * either case to `fallback_label`. Keeping it `Partial` documents that the UI
 * does not assume every locale key is present.
 */
export interface LukeRegion {
  code: string;
  is_national: boolean;
  labels: Partial<Record<Language, string>>;
  fallback_label: string;
}

/**
 * Resolve a region's display label for the active UI locale.
 *
 * SINGLE DOCUMENTED FALLBACK RULE:
 * If the label for `language` is missing or empty, fall back to
 * `region.fallback_label` (the backend-guaranteed canonical Finnish name).
 * This is the ONLY fallback applied — there are no other silent heuristics
 * (no "en-or-fi", no code-as-label). Callers must render exactly this result.
 */
export function resolveRegionLabel(region: LukeRegion, language: Language): string {
  const localized = region.labels?.[language];
  if (localized && localized.trim() !== '') {
    return localized;
  }
  return region.fallback_label;
}

export type WindowMonths = 3 | 12 | 24 | 36;

export interface TimberPriceReportSnapshotRequest {
  region: string;
  harvest_type: string;
  assortment: string;
  window_months: WindowMonths;
  date_from: string;
  date_to: string;
}

/**
 * Directional high/low-vs-history band for the headline read.
 * Where this week's €/m³ sits within its own recent
 * history — a WORDED range, never a shaded area band, and never fabricated.
 */
export type PriceHistoryBand = 'near_high' | 'average' | 'below_average' | 'insufficient_history';

/** Response of GET /api/v1/timber-prices/history-position (computed server-side). */
export interface PriceHistoryPosition {
  band: PriceHistoryBand;
  /** Position (0–100) within the window; null when insufficient. */
  percentile: number | null;
  window_months: WindowMonths;
  /** How many REAL weeks the read is based on. */
  basis_weeks: number;
  latest_week_code: string;
  /** Provenance — always the real backend endpoint (NO-FALLBACK). */
  source: 'backend';
}

// API functions

/**
 * Fetch raw weekly price series for a specific assortment/region/harvest type.
 *
 * skipAuth401 + skipErrorLogging: public read; a 401 must not trigger the
 * global logout cascade.
 */
export async function getWeeklySeries(params: {
  region: string;
  harvest_type: string;
  assortment: string;
  date_from?: string;
  date_to?: string;
  page?: number;
  page_size?: number;
}): Promise<WeeklyPricesResponse> {
  const response = await apiClient.get<WeeklyPricesResponse>('/api/v1/timber-prices/weekly', {
    params,
    skipAuth401: true,
    skipErrorLogging: true,
  });
  return response.data;
}

/**
 * Fetch latest available week prices, optionally filtered by region.
 *
 * skipAuth401 + skipErrorLogging: public read; a 401 must not trigger the
 * global logout cascade.
 */
export async function getLatestPrices(region?: string): Promise<{
  data: LatestPricesData;
  metadata: PriceMetadata;
}> {
  const params: Record<string, string> = {};
  if (region) params.region = region;

  const response = await apiClient.get<{
    data: LatestPricesData;
    metadata: PriceMetadata;
  }>('/api/v1/timber-prices/latest', { params, skipAuth401: true, skipErrorLogging: true });
  return response.data;
}

/**
 * Fetch moving average time series or single value.
 *
 * skipAuth401 + skipErrorLogging: public read; a 401 must not trigger the
 * global logout cascade.
 */
export async function getMovingAverage(params: {
  region: string;
  harvest_type: string;
  assortment: string;
  window_months: WindowMonths;
  date_from?: string;
  date_to?: string;
}): Promise<{
  data: MovingAveragePoint[];
  metadata: PriceMetadata;
}> {
  const response = await apiClient.get<{
    data: MovingAveragePoint[];
    metadata: PriceMetadata;
  }>('/api/v1/timber-prices/moving-average', { params, skipAuth401: true, skipErrorLogging: true });
  return response.data;
}

/**
 * Fetch where this week's price sits within its recent history as a directional
 * band (near_high | average | below_average | insufficient_history), computed
 * server-side. Public endpoint (AllowAny — LUKE prices
 * are public).
 *
 * NO-FALLBACK: the backend returns band 'insufficient_history' + percentile null
 * when the window has too few real weeks, and a 4xx (surfaced here as a thrown
 * error) for unknown region/harvest_type/assortment — it NEVER fabricates a
 * position. Callers treat a thrown error as the honest "history unavailable"
 * state; the page still renders.
 */
export async function getHistoryPosition(params: {
  region: string;
  harvest_type: string;
  assortment: string;
  window_months: WindowMonths;
}): Promise<PriceHistoryPosition> {
  const response = await apiClient.get<{
    data: PriceHistoryPosition;
    metadata: PriceMetadata;
  }>('/api/v1/timber-prices/history-position', {
    params,
    skipAuth401: true,
    skipErrorLogging: true,
  });
  return response.data.data;
}

/** Fetch the one coherent, content-addressed snapshot owned by the report. */
export async function getTimberPriceReportSnapshot(
  params: TimberPriceReportSnapshotRequest,
  signal?: AbortSignal,
): Promise<TimberPriceReportSnapshot> {
  const response = await apiClient.get<TimberPriceReportSnapshot>(
    '/api/v1/timber-prices/report-snapshot',
    {
      params,
      signal,
      skipAuth401: true,
      skipErrorLogging: true,
    },
  );
  return timberPriceReportSnapshotSchema.parse(response.data);
}

/**
 * Fetch resolved pricing scenario (from explicit params or user preference).
 *
 * Authenticated endpoint (backend uses Depends(security)). skipErrorLogging
 * suppresses console.error for non-401 failures; 401s are handled by the
 * standard handle401 interceptor (token refresh + replay).
 */
export async function getScenario(params?: {
  window_months?: WindowMonths;
  region?: string;
}): Promise<{
  data: PricingScenario;
  metadata: PriceMetadata;
}> {
  const response = await apiClient.get<{
    data: PricingScenario;
    metadata: PriceMetadata;
  }>('/api/v1/timber-prices/scenario/', { params, skipErrorLogging: true });
  return response.data;
}

/**
 * Fetch simulation-ready economic context.
 *
 * Authenticated endpoint (backend uses Depends(security)). skipErrorLogging
 * suppresses console.error for non-401 failures; 401s are handled by the
 * standard handle401 interceptor (token refresh + replay).
 */
export async function getScenarioForSimulation(params?: {
  window_months?: WindowMonths;
  region?: string;
}): Promise<{
  data: SimulationContext;
  metadata: PriceMetadata;
}> {
  const response = await apiClient.get<{
    data: SimulationContext;
    metadata: PriceMetadata;
  }>('/api/v1/timber-prices/scenario/for-simulation/', { params, skipErrorLogging: true });
  return response.data;
}

/**
 * Fetch current sync pipeline status (public, no auth required).
 *
 * skipAuth401 + skipErrorLogging: non-critical display data; a 401 must not
 * trigger the global logout cascade.
 */
export async function getSyncStatus(): Promise<{
  data: SyncStatus;
}> {
  const response = await apiClient.get<{
    data: SyncStatus;
  }>('/api/v1/timber-prices/sync-status', {
    skipAuth401: true,
    skipErrorLogging: true,
  });
  return response.data;
}

/**
 * Fetch user's pricing preferences.
 *
 * skipAuth401 + skipErrorLogging: this call is optional — all three callers
 * (TimberPriceReportRoute, useRegionAutoFill, usePlannerData) have catch blocks
 * that fall back to defaults on failure. Without skipAuth401 a 401 from an
 * expired access token triggers handle401, which attempts a token refresh; if
 * that refresh also fails, clearTokensAndRedirect() is called and the user is
 * logged out — even though the failure is non-critical.
 * The bootstrap/planner calls that follow this one are NOT skipAuth401 and will
 * correctly trigger the refresh cascade if the token is truly expired.
 * Guards against a regression where removing skipAuth401 caused a silent
 * logout on Operations Planning page mount when access token was expired.
 */
export async function getPreferences(): Promise<{
  data: UserPricePreferences;
}> {
  const response = await apiClient.get<{
    data: UserPricePreferences;
  }>('/api/v1/timber-prices/preferences/', {
    skipAuth401: true,
    skipErrorLogging: true,
  });
  return response.data;
}

export interface ActiveSnapshotRequestOptions {
  skipErrorLogging?: boolean | ((error: unknown) => boolean);
  skipAuth401?: boolean;
  signal?: AbortSignal;
}

/**
 * Fetch the property's active materialized TimberPriceSnapshot. Its region is
 * the property's price region (municipality), never a user preference.
 */
export async function getActiveSnapshot(
  propertyId: string,
  options?: ActiveSnapshotRequestOptions,
): Promise<{
  data: ActiveSnapshot;
  planning: ActiveSnapshotPlanning | null;
}> {
  const response = await apiClient.get<{
    data: ActiveSnapshot;
    planning: ActiveSnapshotPlanning | null;
  }>('/api/v1/timber-prices/active-snapshot/', {
    ...options,
    params: { property_id: propertyId },
  });
  return response.data;
}

/** Prefix of every property's active-snapshot query key (invalidate all with it). */
export const ACTIVE_SNAPSHOT_QUERY_KEY = ['timber-prices', 'active-snapshot'] as const;

/** The ONE React Query key for a property's active snapshot. */
export function activeSnapshotQueryKey(propertyId: string) {
  return [...ACTIVE_SNAPSHOT_QUERY_KEY, propertyId] as const;
}

/**
 * The one query definition every reader of a property's active snapshot uses.
 * The planner's revaluation, value-growth and lens hooks each fetched it on
 * their own (about 20 reads per plan open, ForestModeler-700f4.15); sharing
 * the key makes them one request that every reader within the window reuses.
 */
export function activeSnapshotQuery(propertyId: string) {
  return {
    queryKey: activeSnapshotQueryKey(propertyId),
    // No abort signal: a reader unmounting must not cancel the one read every
    // other reader is waiting on.
    queryFn: () => getActiveSnapshot(propertyId, { skipAuth401: true, skipErrorLogging: true }),
    staleTime: 30_000,
  };
}

/**
 * Resolve the LUKE timber-price region code for a property's WGS84 coordinates.
 *
 * The backend tries municipality-based resolution first (local data, no I/O),
 * then falls back to coordinate-based resolution via Statistics Finland /
 * Nominatim. Returns null when resolution fails (graceful — no 5xx).
 *
 * skipAuth401 + skipErrorLogging: public endpoint; a 401 must not trigger the
 * global logout cascade, and resolution failures are expected.
 */
export async function resolvePropertyRegion(
  lat: number,
  lng: number,
  municipality?: string,
): Promise<string | null> {
  try {
    const params: Record<string, string | number> = { lat, lng };
    if (municipality && municipality !== 'Unknown') {
      params.municipality = municipality;
    }
    const response = await apiClient.get<{ region_code: string | null }>(
      '/api/v1/timber-prices/resolve-region',
      { params, skipAuth401: true, skipErrorLogging: true },
    );
    return response.data.region_code;
  } catch {
    return null;
  }
}

/**
 * Update user's pricing preferences.
 */
export async function updatePreferences(prefs: {
  default_window_months: WindowMonths;
  default_region_code?: string | null;
}): Promise<{
  data: UserPricePreferences;
}> {
  const response = await apiClient.put<{
    data: UserPricePreferences;
  }>('/api/v1/timber-prices/preferences/', prefs);
  return response.data;
}

/**
 * Fetch the selectable LUKE timber-price regions using the locale-aware label
 * contract. The national aggregate is returned first (its
 * code is the `NATIONAL` sentinel after gateway translation), followed by the
 * non-national regions sorted by Finnish name. Each entry carries
 * `labels` {fi,en,sv,es} + `fallback_label`; resolve a display label with
 * `resolveRegionLabel`.
 *
 * skipAuth401 + skipErrorLogging: public read; a 401 must not trigger the
 * global logout cascade.
 */
export async function getRegions(): Promise<LukeRegion[]> {
  const response = await apiClient.get<LukeRegion[]>('/api/v1/timber-prices/regions', {
    skipAuth401: true,
    skipErrorLogging: true,
  });
  return response.data;
}

// Backward-compatible wrapper

/**
 * Get current prices in a simple format (backward compatible with existing
 * PricingService usage).
 */
export async function getCurrentPrices(region?: string): Promise<Record<string, number>> {
  const { data } = await getLatestPrices(region);
  const flat: Record<string, number> = {};
  for (const [_ht, assortments] of Object.entries(data)) {
    for (const [code, price] of Object.entries(assortments)) {
      const parsed = parsePrice(price);
      if (parsed !== null) {
        flat[code] = parsed;
      }
    }
  }
  return flat;
}

// Display helpers

export const ASSORTMENT_LABELS: Record<string, { fi: string; en: string }> = {
  N1: { fi: 'Mäntytukki', en: 'Pine sawlog' },
  N2: { fi: 'Kuusitukki', en: 'Spruce sawlog' },
  N3: { fi: 'Koivutukki', en: 'Birch sawlog' },
  N4: { fi: 'Mäntykuitupuu', en: 'Pine pulpwood' },
  N5: { fi: 'Kuusikuitupuu', en: 'Spruce pulpwood' },
  N6: { fi: 'Koivukuitupuu', en: 'Birch pulpwood' },
  N7: { fi: 'Mäntypikkutukki', en: 'Pine small log' },
  N8: { fi: 'Kuusipikkutukki', en: 'Spruce small log' },
};

export const HARVEST_TYPE_LABELS: Record<string, { fi: string; en: string }> = {
  '0': { fi: 'Pystykauppa', en: 'Standing sale' },
  '8021': { fi: 'Uudistushakkuu', en: 'Regeneration cut' },
  '8022': { fi: 'Ensiharvennus', en: 'First thinning' },
  '8023': { fi: 'Harvennushakkuu', en: 'Thinning' },
};

export const WINDOW_OPTIONS: { value: WindowMonths; label: Record<Language, string> }[] = [
  { value: 3, label: { fi: '3 kuukautta', en: '3 months', sv: '3 månader', es: '3 meses' } },
  { value: 12, label: { fi: '12 kuukautta', en: '12 months', sv: '12 månader', es: '12 meses' } },
  { value: 24, label: { fi: '24 kuukautta', en: '24 months', sv: '24 månader', es: '24 meses' } },
  { value: 36, label: { fi: '36 kuukautta', en: '36 months', sv: '36 månader', es: '36 meses' } },
];
